import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Video } from 'lucide-react';

const ccUrl = (c, asin) => (c.campaignId
    ? `https://affiliate-program.amazon.com/p/connect/request?adId=${c.campaignId}&campaignId=${c.campaignId}&recc=0&early-acc=0`
    : `https://affiliate-program.amazon.com/p/connect/requests?status=opportunity&type=affiliate-plus&keyword=${asin}`);

const AIPCcModal = ({ asin, title, image, cc, onClose }) => {
    const campaigns = [...(cc?.campaigns || [])].sort((a, b) => (b.commissionPercentage || 0) - (a.commissionPercentage || 0));

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return createPortal(
        <div onClick={onClose} className="fixed inset-0 z-[300] bg-black/60 flex items-center justify-center p-4">
            <div onClick={e => e.stopPropagation()} className="bg-white rounded-xl p-5 w-full max-w-xl max-h-[80vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    {image ? (
                        <img src={image} alt="" className="w-10 h-10 object-contain rounded border border-gray-200 bg-white flex-shrink-0" />
                    ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center flex-shrink-0">
                            <Video size={14} className="text-gray-300" />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h3 className="text-[15px] font-bold text-gray-800">CC Campaigns ({campaigns.length || cc?.count || 0})</h3>
                        <div className="text-xs text-gray-500 truncate">{asin} · {title}</div>
                    </div>
                    <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700">
                        <X size={20} />
                    </button>
                </div>

                {/* List */}
                {campaigns.length > 0 ? (
                    <div className="flex flex-col gap-2.5">
                        {campaigns.map((c, i) => {
                            const daysLeft = c.endMs ? Math.max(0, Math.ceil((c.endMs - Date.now()) / 86400000)) : null;
                            return (
                                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-semibold text-gray-800 truncate">
                                            {c.campaignName || c.brandName || 'Campaign'}
                                        </div>
                                        <div className="text-[11px] font-bold text-gray-500">
                                            {c.commissionPercentage != null ? `${c.commissionPercentage}%` : ''}
                                            {daysLeft != null ? ` • ${daysLeft}d left` : ''}
                                            {c.remainingBudget != null ? ` • $${Number(c.remainingBudget).toLocaleString()} left` : ''}
                                        </div>
                                    </div>
                                    <a href={ccUrl(c, asin)} target="_blank" rel="noopener noreferrer"
                                        className="ml-3 flex-shrink-0 px-3.5 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-md hover:bg-blue-700">
                                        Open
                                    </a>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-6">
                        <p className="text-sm text-gray-500 mb-3">
                            {cc?.count || 0} campaign{cc?.count === 1 ? '' : 's'}{cc?.highestCommission != null ? `, up to ${cc.highestCommission}%` : ''}
                        </p>
                        <a href={ccUrl({}, asin)} target="_blank" rel="noopener noreferrer"
                            className="inline-block px-4 py-2 text-xs font-semibold bg-blue-600 text-white rounded-md hover:bg-blue-700">
                            Open in Creator Connections
                        </a>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default AIPCcModal;